/**
 * The business playbook on the Strategy Hub: what every agent writes from (business, voice, industry rules, what to write about).
 * One card, three things to do: "Build it for me" (reads the website and proposes the empty sections), Approve / Discard a proposal,
 * Edit a section. Data: /api/workspaces/:wsId/business-playbook (server/playbook.ts). The same works in Draper's chat
 * ("build my playbook", "approve the playbook", "never say guaranteed").
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Check, CircleDashed, Loader2, Pencil, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

type Value = string | string[] | boolean | null;
interface FieldDef { key: string; label: string; type: "text" | "list" | "bool"; hint?: string; ownerOnly?: boolean }
interface Section {
  key: string; title: string; why: string; fields: FieldDef[];
  status: "missing" | "proposed" | "set";
  data: Record<string, Value>;
  proposal: { data: Record<string, Value>; builtAt: string; source: string } | null;
}
interface Playbook { businessName: string; website: string; sections: Section[]; ready: boolean; nextStep: string; questions: string[]; builtAt: string | null }

const show = (v: Value | undefined): string => (Array.isArray(v) ? v.join(" · ") : typeof v === "boolean" ? (v ? "Yes" : "No") : String(v ?? ""));
const isEmpty = (v: Value | undefined) => (Array.isArray(v) ? v.length === 0 : v === null || v === undefined || v === "");

function StatusPill({ s }: { s: Section }) {
  if (s.status === "set") return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-500"><Check size={11} /> In use</span>;
  if (s.status === "proposed") return <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-500"><Sparkles size={11} /> Proposal waiting</span>;
  return <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"><CircleDashed size={11} /> Not set</span>;
}

function Values({ fields, data, testId }: { fields: FieldDef[]; data: Record<string, Value>; testId: string }) {
  const rows = fields.filter((f) => !isEmpty(data[f.key]));
  if (!rows.length) return <p className="text-xs text-muted-foreground">Nothing yet.</p>;
  return (
    <dl className="space-y-1.5 text-sm" data-testid={testId}>
      {rows.map((f) => (
        <div key={f.key} className="grid grid-cols-1 sm:grid-cols-[12rem_1fr] gap-x-3">
          <dt className="text-xs text-muted-foreground pt-0.5">{f.label}</dt>
          <dd className="min-w-0 break-words">{show(data[f.key])}</dd>
        </div>
      ))}
    </dl>
  );
}

function Editor({ section, onSave, onCancel, saving }: { section: Section; onSave: (d: Record<string, Value>) => void; onCancel: () => void; saving: boolean }) {
  const [d, setD] = useState<Record<string, Value>>(() => ({ ...section.data }));
  return (
    <div className="space-y-3">
      {section.fields.map((f) => (
        <label key={f.key} className="block space-y-1">
          <span className="text-xs text-muted-foreground">{f.label}{f.type === "list" ? " (one per line)" : ""}{f.hint ? ` · ${f.hint}` : ""}</span>
          {f.type === "bool" ? (
            <select value={d[f.key] === true ? "yes" : d[f.key] === false ? "no" : ""} onChange={(e) => setD({ ...d, [f.key]: e.target.value === "" ? null : e.target.value === "yes" })} className="h-8 rounded-md border border-border bg-background px-2 text-sm">
              <option value="">Not set</option><option value="yes">Yes</option><option value="no">No</option>
            </select>
          ) : f.type === "list" ? (
            <Textarea rows={3} value={Array.isArray(d[f.key]) ? (d[f.key] as string[]).join("\n") : ""} onChange={(e) => setD({ ...d, [f.key]: e.target.value.split("\n") })} className="text-sm" data-testid={`playbook-input-${section.key}-${f.key}`} />
          ) : (
            <Input value={String(d[f.key] ?? "")} onChange={(e) => setD({ ...d, [f.key]: e.target.value })} className="h-8 text-sm" data-testid={`playbook-input-${section.key}-${f.key}`} />
          )}
        </label>
      ))}
      <div className="flex gap-2">
        <Button size="sm" onClick={() => onSave({ ...d, ...Object.fromEntries(section.fields.filter((f) => f.type === "list").map((f) => [f.key, ((d[f.key] as string[]) ?? []).map((x) => x.trim()).filter(Boolean)])) })} disabled={saving} data-testid={`button-playbook-save-${section.key}`}>
          {saving ? <Loader2 className="animate-spin" /> : <Check />} Save
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export default function PlaybookCard({ workspaceId }: { workspaceId: number | null }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const base = `/api/workspaces/${workspaceId}/business-playbook`;
  const key = ["/api/workspaces", workspaceId, "business-playbook"];
  const [editing, setEditing] = useState<string | null>(null);
  const { data: pb, isLoading } = useQuery<Playbook>({
    queryKey: key, enabled: !!workspaceId,
    queryFn: async () => { const r = await fetch(base, { credentials: "include" }); if (!r.ok) throw new Error("Couldn't load the playbook"); return r.json(); },
  });
  const call = async (path: string, method: string, body?: unknown) => {
    const r = await fetch(`${base}${path}`, { method, credentials: "include", headers: { "Content-Type": "application/json" }, body: body ? JSON.stringify(body) : undefined });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j?.message || "Something went wrong");
    return j;
  };
  const done = (title: string, description?: string) => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ["/api/workspaces", workspaceId, "content-matrix"] }); toast({ title, description }); };
  const fail = (e: any) => toast({ title: "Not done", description: e?.message, variant: "destructive" });

  const build = useMutation({ mutationFn: () => call("/build", "POST", {}), onSuccess: (j: any) => done(j.proposed?.length ? "Proposals ready" : "Nothing new to propose", j.note || (j.proposed?.length ? "Review each one below and approve it. Nothing is used until you do." : undefined)), onError: fail });
  const approve = useMutation({ mutationFn: (section: string) => call("/approve", "POST", { section }), onSuccess: () => done("Approved", "Every agent uses it from the next piece it writes."), onError: fail });
  const discard = useMutation({ mutationFn: (section: string) => call("/discard", "POST", { section }), onSuccess: () => done("Proposal discarded"), onError: fail });
  const save = useMutation({ mutationFn: ({ section, data }: { section: string; data: Record<string, Value> }) => call(`/sections/${section}`, "PUT", { data }), onSuccess: () => { setEditing(null); done("Saved", "Agents use it from the next piece they write."); }, onError: fail });

  if (!workspaceId) return null;
  const waiting = pb?.sections.filter((s) => s.proposal) ?? [];
  return (
    <div className="rounded-2xl border border-border bg-card/50 p-5 space-y-5" data-testid="playbook-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2"><BookOpen size={16} className="text-violet-400" /><h3 className="text-sm font-display font-bold text-foreground">Business playbook</h3></div>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">Everything Draper, Peggy, Rogers, Pete and Harry write from. DrPR proposes, you approve; nothing it proposes is used until you do.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {waiting.length > 1 && <Button size="sm" variant="outline" onClick={() => approve.mutate("all")} disabled={approve.isPending} data-testid="button-playbook-approve-all"><Check /> Approve all</Button>}
          <Button size="sm" onClick={() => build.mutate()} disabled={build.isPending} data-testid="button-playbook-build">
            {build.isPending ? <Loader2 className="animate-spin" /> : <Sparkles />} {build.isPending ? "Reading your website…" : "Build it for me"}
          </Button>
        </div>
      </div>

      {isLoading || !pb ? <Loader2 className="animate-spin text-muted-foreground" /> : (
        <>
          <div className={`rounded-lg px-3 py-2 text-sm ${pb.ready ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-violet-500/10 text-foreground"}`} data-testid="playbook-next-step">
            <span className="font-medium">Next: </span>{pb.nextStep}
          </div>

          {pb.questions.length > 0 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 space-y-1" data-testid="playbook-questions">
              <div className="text-xs font-semibold text-amber-600 dark:text-amber-400">Only you can answer these (edit the section, or tell Draper):</div>
              <ul className="list-disc pl-5 text-sm space-y-0.5">{pb.questions.map((q, i) => <li key={i}>{q}</li>)}</ul>
            </div>
          )}

          <div className="divide-y divide-border">
            {pb.sections.map((s) => (
              <section key={s.key} className="py-4 first:pt-0 last:pb-0 space-y-3" data-testid={`playbook-section-${s.key}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2"><h4 className="text-sm font-semibold">{s.title}</h4><StatusPill s={s} /></div>
                  {editing !== s.key && <Button size="sm" variant="ghost" onClick={() => setEditing(s.key)} data-testid={`button-playbook-edit-${s.key}`}><Pencil size={14} /> Edit</Button>}
                </div>
                <p className="text-xs text-muted-foreground -mt-2">{s.why}</p>
                {editing === s.key
                  ? <Editor section={s} saving={save.isPending} onCancel={() => setEditing(null)} onSave={(data) => save.mutate({ section: s.key, data })} />
                  : <Values fields={s.fields} data={s.data} testId={`playbook-values-${s.key}`} />}
                {s.proposal && (
                  <div className="rounded-lg border border-dashed border-amber-500/50 bg-amber-500/5 p-3 space-y-2" data-testid={`playbook-proposal-${s.key}`}>
                    <div className="text-xs font-semibold text-amber-600 dark:text-amber-400">Proposed from {s.proposal.source} · not in use until you approve{s.status === "set" ? " (it is added to what you have; nothing filled is replaced)" : ""}</div>
                    <Values fields={s.fields} data={s.proposal.data} testId={`playbook-proposal-values-${s.key}`} />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => approve.mutate(s.key)} disabled={approve.isPending} data-testid={`button-playbook-approve-${s.key}`}><Check /> Approve</Button>
                      <Button size="sm" variant="ghost" onClick={() => discard.mutate(s.key)} disabled={discard.isPending} data-testid={`button-playbook-discard-${s.key}`}><X /> Discard</Button>
                    </div>
                  </div>
                )}
              </section>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
